import { Banner, Button, UiLink } from '@nexus/ui';
import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useSession } from '../session/session';
import { loginSecurityQuery } from '../settings/login-security';
import { useGuardedSignOut } from './sign-out';

/**
 * Privileged permissions are locked in a session without MFA. After a password login the banner
 * says what unlocks them: setting up a second factor, or, when the account already has a passkey,
 * a new login with it. A passkey added later does not turn the running session into one with MFA.
 */
export function MfaBanner() {
  const { t } = useTranslation();
  const session = useSession();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const passwordSession = session.method === 'password';
  const security = useQuery({
    ...loginSecurityQuery,
    enabled: session.missingMfa && passwordSession,
  }).data;
  const [signOut, signOutDialog] = useGuardedSignOut();
  if (!session.missingMfa) return null;

  const hasPasskey = passwordSession && (security?.passkeys.length ?? 0) > 0;
  let action;
  if (hasPasskey) {
    action = (
      <Button size="sm" onClick={signOut}>
        {t('shell.mfaSignInAgain')}
      </Button>
    );
  } else if (passwordSession && path !== '/profile') {
    action = (
      <Button asChild size="sm">
        <UiLink href="/profile">{t('shell.mfaSetUp')}</UiLink>
      </Button>
    );
  }

  return (
    <>
      <Banner tone="info" layout="bar" icon="shield-alert" action={action}>
        {t(
          hasPasskey
            ? 'shell.mfaBannerPasskey'
            : passwordSession
              ? 'shell.mfaBannerPassword'
              : 'shell.mfaBanner',
        )}
      </Banner>
      {signOutDialog}
    </>
  );
}
